/* ══════════════════════════════════════════════════════════════
   Strategy Card — Compact Strategy Preview
   Shows current regime + signal + confidence on Dashboard
   ══════════════════════════════════════════════════════════════ */

import { fetchMLStrategy } from '../services/ml-api';
import { renderUnavailableBlock } from './data-state';

export interface StrategyCardData {
  regime: string;
  regimeEmoji: string;
  strategy: string;
  confidence: number;
  riskLevel: string;
  entry: { min: number; max: number };
  target: { min: number; max: number };
  stopLoss: number;
  outlook: string;
}

const RISK_COLORS: Record<string, string> = {
  low: 'var(--india-green)',
  moderate: 'var(--india-saffron)',
  high: 'var(--india-red)',
  extreme: '#ff0040',
};

const REGIME_MAP: Record<string, { emoji: string; label: string }> = {
  'TRENDING_BULL': { emoji: '🐂', label: 'Trending Bull' },
  'TRENDING_BEAR': { emoji: '🐻', label: 'Trending Bear' },
  'MEAN_REVERTING': { emoji: '➡️', label: 'Mean Reverting' },
  'HIGH_VOLATILITY': { emoji: '🌊', label: 'High Volatility' },
  'trending_bull': { emoji: '🐂', label: 'Trending Bull' },
  'trending_bear': { emoji: '🐻', label: 'Trending Bear' },
  'sideways': { emoji: '➡️', label: 'Sideways / Range' },
  'high_volatility': { emoji: '🌊', label: 'High Volatility' },
};

function renderWithData(container: HTMLElement, d: StrategyCardData): void {
  const riskColor = RISK_COLORS[d.riskLevel] || RISK_COLORS.moderate;
  const confidenceColor = d.confidence >= 70 ? 'var(--india-green)' : d.confidence >= 50 ? 'var(--india-saffron)' : 'var(--india-red)';

  container.innerHTML = `
    <div class="glass-card strategy-card-compact">
      <div class="card-header">
        <h3>📊 Today&apos;s Setup</h3>
        <span class="regime-badge">${d.regimeEmoji} ${d.regime}</span>
      </div>
      <div class="strategy-signal">
        <div class="strategy-signal__copy">
          <span class="signal-name">${d.strategy}</span>
          <span class="strategy-signal__outlook">${d.outlook.toUpperCase()} BIAS</span>
        </div>
        <div class="confidence-ring" style="--confidence: ${d.confidence}; --conf-color: ${confidenceColor}">
          <span class="confidence-value">${d.confidence}%</span>
        </div>
      </div>
      <div class="strategy-levels">
        <div class="level-item entry">
          <span class="level-label">Entry</span>
          <span class="level-value">₹${d.entry.min.toLocaleString('en-IN')} - ${d.entry.max.toLocaleString('en-IN')}</span>
        </div>
        <div class="level-item target">
          <span class="level-label">Tgt</span>
          <span class="level-value">₹${d.target.min.toLocaleString('en-IN')} - ${d.target.max.toLocaleString('en-IN')}</span>
        </div>
        <div class="level-item stoploss">
          <span class="level-label">SL</span>
          <span class="level-value">₹${d.stopLoss.toLocaleString('en-IN')}</span>
        </div>
      </div>
      <div class="strategy-footer">
        <span class="risk-badge" style="background: ${riskColor}20; color: ${riskColor}">${d.riskLevel.toUpperCase()} RISK</span>
        <a href="#/strategy" class="view-details">View Details →</a>
      </div>
    </div>`;
}

export function renderStrategyCard(container: HTMLElement, data?: StrategyCardData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = `<div class="glass-card strategy-card-compact" style="padding:2rem;text-align:center;">Loading strategy...</div>`;
    fetchMLStrategy().then(strat => {
      if (strat) {
        const regimeInfo = REGIME_MAP[strat.regime] || { emoji: '📊', label: strat.regime };
        const riskLevel = strat.riskLevel || (strat.riskReward >= 2 ? 'low' : strat.riskReward >= 1.5 ? 'moderate' : 'high');
        const mapped: StrategyCardData = {
          regime: regimeInfo.label,
          regimeEmoji: regimeInfo.emoji,
          strategy: strat.strategyLabel || strat.strategy?.toUpperCase() || 'MOMENTUM LONG',
          confidence: strat.confidence || 0,
          riskLevel,
          entry: { min: strat.entry?.min || 0, max: strat.entry?.max || 0 },
          target: { min: strat.target?.min || 0, max: strat.target?.max || 0 },
          stopLoss: strat.stopLoss || 0,
          outlook: strat.regime?.includes('bull') ? 'bullish' : strat.regime?.includes('bear') ? 'bearish' : 'neutral',
        };
        renderWithData(container, mapped);
      } else {
        container.innerHTML = renderUnavailableBlock('glass-card strategy-card-compact', 'Live strategy unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('glass-card strategy-card-compact', 'Live strategy unavailable');
    });
  }
}
